import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, featuredComics } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();

  const currentCategory = useMemo(() => {
    if (!slug || slug === 'all') return null;
    return categories.find((c) => c.slug === slug) || null;
  }, [slug]);

  const filteredComics = useMemo(() => {
    if (!slug || slug === 'all') return featuredComics;
    return featuredComics.filter((c) => c.categorySlug === slug);
  }, [slug]);

  const pageTitle = currentCategory ? currentCategory.name : '全部分类';
  const pageDesc = currentCategory
    ? currentCategory.description
    : '浏览全部漫画分类，发现属于你的精彩故事';

  // Structured breadcrumb data
  const breadcrumbItems = [
    { label: '首页', to: '/' },
    ...(currentCategory ? [{ label: '全部分类', to: '/category/all' }] : []),
    { label: pageTitle, to: null as string | null },
  ];

  // SEO structured data
  const breadcrumbLD = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems.map((item, idx) => ({
      '@type': 'ListItem' as const,
      position: idx + 1,
      name: item.label,
      ...(item.to ? { item: `http://xudongprc.com/${item.to}` } : {}),
    })),
  };

  return (
    <>
      <Navbar />
      <main className="pt-20 md:pt-24 min-h-screen bg-background-50">
        {/* Breadcrumb JSON-LD */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLD) }}
        />

        <div className="px-4 md:px-6 max-w-7xl mx-auto py-10">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="面包屑导航">
            {breadcrumbItems.map((item, idx) => (
              <span key={idx} className="flex items-center gap-2">
                {idx > 0 && <i className="ri-arrow-right-s-line text-xs"></i>}
                {item.to ? (
                  <Link to={item.to} className="hover:text-primary-500 transition-colors">
                    {item.label}
                  </Link>
                ) : (
                  <span className="text-foreground-800 font-medium">{item.label}</span>
                )}
              </span>
            ))}
          </nav>

          {/* Page header */}
          <div className="mb-10">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 font-heading mb-3">
              {currentCategory ? (
                <>{currentCategory.name}<span className="text-accent-500 ml-2">·</span> <span className="text-foreground-500 font-normal text-xl">{currentCategory.count}部作品</span></>
              ) : (
                <>漫画<span className="text-accent-500">分类</span></>
              )}
            </h1>
            <p className="text-sm text-foreground-500 max-w-xl">
              {pageDesc}
            </p>
          </div>

          {/* Category tabs — only show on "all" or when switching is useful */}
          <div className="mb-10">
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to="/category/all"
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  !slug || slug === 'all'
                    ? 'bg-accent-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                }`}
              >
                全部 ({featuredComics.length})
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                    slug === cat.slug
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                  }`}
                >
                  {cat.name} ({cat.count})
                </Link>
              ))}
            </div>
          </div>

          {/* Comics grid */}
          {filteredComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {filteredComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="group cursor-pointer"
                >
                  <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-100 mb-3">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      title={comic.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                    {comic.title}
                  </h3>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-foreground-500">{comic.author}</p>
                    <div className="flex items-center gap-1">
                      <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                      <span className="text-xs text-foreground-600">{comic.rating}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] text-foreground-400 px-1.5 py-0.5 bg-background-100 rounded-full whitespace-nowrap">
                      {comic.status}
                    </span>
                    <span className="text-[10px] text-foreground-400">
                      {comic.latestChapter}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto bg-background-100 rounded-2xl flex items-center justify-center mb-4">
                <i className="ri-emotion-sad-line text-foreground-300 text-2xl"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类下暂无漫画作品</p>
              <Link
                to="/category/all"
                className="inline-flex items-center gap-2 mt-4 px-5 py-2 bg-accent-500 text-background-50 rounded-full text-sm font-medium hover:bg-accent-600 transition-all whitespace-nowrap cursor-pointer"
              >
                浏览全部作品
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          )}

          {/* Category description section — shown only on specific category pages */}
          {currentCategory && (
            <div className="mt-16 bg-background-100 rounded-2xl p-6 md:p-8">
              <h2 className="text-lg font-bold text-foreground-950 font-heading mb-3">
                关于{currentCategory.name}
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed max-w-3xl">
                {currentCategory.description}。古风漫画网精选{currentCategory.count}部{currentCategory.name}类韩国漫画，
                每日实时更新，高清画质免费阅读。采用流畅的下拉式观看模式，为您带来沉浸式的漫画阅读体验。
              </p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}